import { useState, useCallback, useEffect } from 'react';

// ============================================================================
// TYPES
// ============================================================================

export type ToastVariant = 'default' | 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  title?: string;
  description?: string;
  variant?: ToastVariant;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

export interface ToastOptions extends Omit<Toast, 'id'> {}

// ============================================================================
// TOAST STATE
// ============================================================================

const TOAST_LIMIT = 5;
const TOAST_REMOVE_DELAY = 5000;

let count = 0;

function generateId() {
  count = (count + 1) % Number.MAX_VALUE;
  return count.toString();
}

// Global state for toasts
type ToastState = {
  toasts: Toast[];
  addToast: (toast: ToastOptions) => string;
  removeToast: (id: string) => void;
  clearToasts: () => void;
};

const listeners: Array<(state: ToastState) => void> = [];

let memoryState: ToastState = {
  toasts: [],
  addToast: () => '',
  removeToast: () => {},
  clearToasts: () => {},
};

function dispatch(newToasts: Toast[]) {
  memoryState = {
    ...memoryState,
    toasts: newToasts,
  };
  listeners.forEach((listener) => {
    listener(memoryState);
  });
}

// ============================================================================
// TOAST FUNCTION
// ============================================================================

export function toast(options: ToastOptions): string {
  const id = generateId();
  const newToast: Toast = {
    id,
    variant: 'default',
    duration: TOAST_REMOVE_DELAY,
    ...options,
  };

  dispatch([...memoryState.toasts, newToast].slice(-TOAST_LIMIT));

  // Auto-remove after duration
  if (newToast.duration && newToast.duration > 0) {
    setTimeout(() => {
      dismissToast(id);
    }, newToast.duration);
  }

  return id;
}

export function dismissToast(id: string) {
  dispatch(memoryState.toasts.filter((t) => t.id !== id));
}

export function clearToasts() {
  dispatch([]);
}

// ============================================================================
// USE TOAST HOOK
// ============================================================================

export function useToast() {
  const [state, setState] = useState(memoryState);

  useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, []);

  const addToast = useCallback((options: ToastOptions) => {
    return toast(options);
  }, []);

  const removeToast = useCallback((id: string) => {
    dismissToast(id);
  }, []);

  return {
    toasts: state.toasts,
    toast: addToast,
    dismiss: removeToast,
    clear: clearToasts,
  };
}
